import { BoundingBox } from '@mediapipe/tasks-vision';
import {
	IMessages,
	IOperation,
	IPageWarning,
	MODEL_DOCUMENT_CATEGORY,
	OPERATION_STATUS,
	TestStatus,
} from './user-identification';
import { ERROR_CODE, ERROR_STATUS } from 'src/app/shared/models/error.models';
import { IConsecutiveCells, IGridAnalysis } from 'src/app/core/models/image.models';
import { IAlert } from 'src/app/core/models/alert.models';

// Segundos de los siguientes controles
export const OCR_WARNING_TIME: number = 90;
export const OCR_SEMI_MANUAL_TIME: number = 60;
export const OCR_FINISH_TIME: number = 2;
export const SEG_HISTORY_INTERVAL: number = 0.5; // NUNCA MAYOR A 1 SEG

/**
 * Aspecto del contenedor `tec-scan` en desktop (33rem×21rem en `ocr.component.scss`).
 * Solo fallback en `getAnalysisViewportAspect` cuando aún no hay layout medido en DOM.
 */
export const OCR_SCAN_PREVIEW_ASPECT = 33 / 21;

export interface IOcrCannyConf {
	document: IOcrCanny;
	general: {
		startAt: string;
		finishAt: string;
		controlValidation: boolean;
		// maxTime: number; // seg
		messages: IMessages;
		tries: number;
		countTries: number;
		status: TestStatus;
		complete: boolean;
	};
}

export interface IOcrConf {
	document: IOcr[];
	general: {
		startAt: string;
		finishAt: string;
		timeBetweenTest: number; // seg
		// maxTime: number; // seg
		messages: IMessages;
		tries: number;
		countTries: number;
		status: TestStatus;
		complete: boolean;
		controlValidation: boolean;
		minPercenteageTimeInMiddle: number;
		minSharpnessLapacian: number; // Puntuación minima de nitidez
		minSharpnessFourier: number; // Puntuación minima de nitidez
		maxConsecutiveCellsWith95: number; // Número mínimo de celdas consecutivas al 95% de brillo
		maxConsecutiveCellsWith98: number; // Número mínimo de celdas consecutivas al 98% de brillo
	};
}

export interface IOcrCanny {
	documentTestId: DOCUMENT_CHECK_ID;
	photos: Array<IDocumentTestPhoto>;
	// results: Array<IDocumentTestResult>;
	status: TestStatus;
	complete: boolean;
	startAt: string;
	finishAt: string;
	maxTime: number;
	// timeConf: ITestTimeConf;
	messages: IMessages;
	tries: number;
	countTries: number;
	minSharpness: number; // Puntuación minima de nitidez
}

export interface IOcr {
	documentTestId: DOCUMENT_CHECK_ID;
	photos: Array<IDocumentTestPhoto>;
	status: TestStatus;
	complete: boolean;
	startAt: string;
	finishAt: string;
	maxTime: number;
	minTime: number;
	// timeConf: ITestTimeConf;
	messages: IMessages;
	tries: number;
	countTries: number;
	minPercentageDocument: number; // Porcentaje mínimo para dar el documento como válida
	showAnimation: boolean;
}

export enum DOCUMENT_CHECK_ID {
	FRONT = 'front',
	BACK = 'back',
}

export interface IOcrPayload {
	image?: string;
	cameras: Array<string>;
	device_id: string;
	data: any;
	status: boolean;
	/** Solo presente cuando el documento detectado/seleccionado es pasaporte (NIF-293). */
	is_passport?: boolean;
	/**
	 * Método real de captura Flexible (NIF-921): UPLOAD | PHOTO | SCAN.
	 * MIDNI/EUDI los persiste el back en sus flujos.
	 */
	capture_method?: 'UPLOAD' | 'PHOTO' | 'SCAN';
	/** Bbox cara MediaPipe 0–1 respecto al JPEG de `image` (NIF-536; solo si obfuscation_photo). */
	face_bbox?: IFaceBboxNorm;
}

/** Coordenadas normalizadas 0–1 respecto al JPEG enviado a POST /ocr. */
export interface IFaceBboxNorm {
	left: number;
	top: number;
	right: number;
	bottom: number;
}

export interface IOcrManualPayload extends IOcrPayload {
	type: 'DNI_ESP' | 'TARJ_RESID' | 'PASSPORT';
}

export interface IBarcodeComparison {
	document_number?: { matched: boolean; ocr?: string; barcode?: string };
}

export interface IBarcodeState {
	required_scan: boolean;
	read: boolean;
	verified: boolean;
	retry: boolean;
	/** true si se omitió el barcode tras max attempts (OCR básico ya OK). */
	skipped?: boolean;
	parser?: string | null;
	comparison?: IBarcodeComparison | null;
	attempts?: number;
	max_attempts?: number;
}

export interface IBarcodeReadPayload {
	status: boolean;
	image?: string;
	device_id: string;
	cameras: Array<string>;
	data?: string;
	/** true si el front ya recortó la franja barcode (servidor debe skip_crop en ICR). */
	cropped?: boolean;
	/** true (default) → ICR hace upscale 2× antes del decode; false para desactivar. */
	upscale?: boolean;
}

/** Emisión de captura barcode: auto = frame completo; manual = franja + quiet zone. */
export interface IBarcodeCapturedFrame {
	image: string;
	cropped: boolean;
}

export interface IBarcodeReadResponse {
	code?: number;
	result: OPERATION_STATUS.OK | OPERATION_STATUS.KO;
	barcode?: IBarcodeState;
	process?: IOperation[];
	process_uuid?: string;
	retry?: boolean;
	ocr_completed?: boolean;
	required_other_side?: boolean;
	required_side?: 'front' | 'back';
	process_async?: boolean;
}

export interface IOcrResponse {
	code: number;
	result: OPERATION_STATUS.OK | OPERATION_STATUS.KO;
	required_other_side: boolean;
	required_side: 'front' | 'back';
	retry: boolean;
	process: IOperation[];
	barcode?: IBarcodeState;
}

export interface IOcrErrorResponse {
	code: ERROR_STATUS;
	error: {
		error_code: ERROR_CODE;
		msg: string;
		/** Copy catálogo BBDD (locale petición); p. ej. 2038. */
		title?: string;
		message?: string;
		/** ISO-2/3 del país rechazado (código 2071); si falta se intenta extraer de `msg`. */
		country?: string;
	};
	result: OPERATION_STATUS.OK | OPERATION_STATUS.KO;
	retry: boolean;
	/** El back puede indicar el siguiente lado a capturar también en KO (p. ej. MRZ 2006). */
	required_other_side?: boolean;
	required_side?: 'front' | 'back' | null;
	current_data?: string;
}

export interface IControlRectPercentage {
	extHorizontally: number;
	extVertically: number;
	intHorizontally: number;
	intVertically: number;
}

export interface IDocumentResult {
	detected: boolean;
	categoryName: MODEL_DOCUMENT_CATEGORY;
	score: number;
}

export interface IDocumentTestPhoto {
	img: string;
	lapacian: number;
	fourier: number;
	label: string;
	docSize: BoundingBox;
	selected: boolean;
	gridAnalysisBrightness: IGridAnalysis;
	consecutiveCellsWith95: IConsecutiveCells[];
	consecutiveCellsWith98: IConsecutiveCells[];
	/** FACE remapeada al JPEG; solo si el workflow ofusca photo. */
	faceBboxNorm?: IFaceBboxNorm;
}

export interface IDocumentDetection {
	name: MODEL_DOCUMENT_CATEGORY;
	score: number;
	boundingBox: BoundingBox;
}

export interface IOcrManualConf {
	document: IOcrManualDocument[];
	general: {
		startAt: string;
		finishAt: string;
		maxTime: number;
		status: TestStatus;
		observations?: Array<string>;
	};
}

export interface IOcrManualDocument {
	documentTestId: DOCUMENT_CHECK_ID;
	photos: Array<{ img: string; label: string; faceBboxNorm?: IFaceBboxNorm }>;
	status: TestStatus;
	complete: boolean;
	startAt: string;
	finishAt: string;
	maxTime: number;
	minTime: number;
}

export interface IOcrHistory {
	timestamp: string;
	documentCentered: boolean;
	pageWarning: IPageWarning;
	documentDetection: boolean;
	isSharpValid: boolean;
	isBrightnessValid: boolean;
	animation: string;
	speech: string;
	isTooClose: boolean;
}

export interface IOcrDocumentStatus {
	id: string;
	repeatTime: number;
	promptConfig: IOcrPromptConfig[];
	conditions: () => boolean;
}

export interface IOcrPromptConfig {
	audio: string;
	centerText: string;
	alert: IAlert;
	keepPrevCenterText?: boolean; // Si es true, conserva el centerText que ya haya en pantalla en lugar de sobreescribirlo
}
